import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { processIndices } from "../utils/intersectionHelpers";
import { createCachedMaterialsStackedLaserCut } from "../utils/materialManager";
import { initializeThreeScene } from "../utils/initializeThreeScene";
import { generateSVG, downloadSVG } from "../utils/exportUtils";
import useVisualHelpers from "../hooks/useVisualHelpers";
import { hull } from "../utils/hull";
import "../styles/common.css";
import TutorialWindow from "../components/TutorialWindow";
import { stackMoldTutorial } from "../utils/tutorialContents";
import { useSyncedCamera } from "../hooks/useSyncedCamera";
import { normalizeModel } from "../utils/normalizeModel";

const StackMold = ({ stlFile }) => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const { createVisualization, removeVisualHelpers } = useVisualHelpers();
  const gridHelperRef = useRef(null);
  const helpersGroupRef = useRef(null);

  // State variables
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [svgReady, setSvgReady] = useState(false);
  const [modelDimensions, setModelDimensions] = useState(null);
  const [materialThickness, setMaterialThickness] = useState(3);
  const [maxLayerCount, setMaxLayerCount] = useState(20);
  const [layerCount, setLayerCount] = useState(20);
  const [expanded, setExpanded] = useState(false);
  const [processingStatus, setProcessingStatus] = useState("");
  const [addThreadedRods, setAddThreadedRods] = useState(false);
  const [rodPositions, setRodPositions] = useState([]);
  const [tutorialVisible, setTutorialVisible] = useState(false);
  const pageId = useMemo(() => `stacked-layers`, []);
// 手工流程通常较慢（叠层切割+粘/夹+打磨/找平）
const fastIterNotRecommended = useMemo(() => true, []);

  const [settingsOpen, setSettingsOpen] = useState(true);
  useSyncedCamera(pageId, cameraRef, controlsRef);

  // Refs for optimizing slider performance
  const isSliderDraggingRef = useRef(false);
  const currentLayerCountRef = useRef(20);
  const regenerateLayersTimeoutRef = useRef(null);

  // Object references
  const objRef = useRef(null);
  const layersRef = useRef([]);
  const shapesRef = useRef([]);

  // Cached materials for reuse
  const cachedMaterials = useMemo(
    () => createCachedMaterialsStackedLaserCut(),
    []
  );

  const waxMaterial = useMemo(
    () =>
      new THREE.MeshPhongMaterial({
        color: 0x0a7a32, // 深绿色
        transparent: true,
        opacity: 0.7, // 看得见内部关系
        shininess: 40,
        specular: 0x224422,
        side: THREE.DoubleSide,
      }),
    []
  );

  const toggleTutorial = useCallback(() => {
    setTutorialVisible((prev) => !prev);
  }, []);

  // Calculate maximum layer count based on model height and material thickness
  const calculateMaxLayerCount = useCallback((modelHeight, thickness) => {
    if (!modelHeight || !thickness) return 20; // Default if dimensions not available
    const calculatedCount = Math.floor(modelHeight / thickness);
    return Math.max(3, Math.min(calculatedCount, 50));
  }, []);

  // Initialize grid and axes helpers
  const initHelpers = useCallback(() => {
    if (!sceneRef.current) return;

    if (!gridHelperRef.current) {
      const gridHelper = new THREE.GridHelper(200, 20, 0x444444, 0x888888);
      gridHelper.position.y = 0;
      gridHelper.material.opacity = 1.0;
      gridHelper.material.transparent = true;
      sceneRef.current.add(gridHelper);
      gridHelperRef.current = gridHelper;

      const axesHelper = new THREE.AxesHelper(100);
      sceneRef.current.add(axesHelper);

      //console.log("Grid and axes helpers initialized");
    }
  }, []);

  // Clear previous objects
  const clearPreviousObjects = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    // Preserve Grid and Axes Helpers
    if (gridHelperRef.current) {
      scene.remove(gridHelperRef.current);
      gridHelperRef.current = null;
    }

    if (helpersGroupRef.current) {
      removeVisualHelpers(scene);
      helpersGroupRef.current = null;
    }

    // Helper function to remove and dispose objects
    const cleanupObjects = (objects) => {
      objects.forEach((obj) => {
        if (obj !== gridHelperRef.current) {
          // Ensure grid isn't removed
          scene.remove(obj);
          if (obj.geometry) obj.geometry.dispose();
          if (obj.material) {
            if (Array.isArray(obj.material)) {
              obj.material.forEach((m) => m.dispose());
            } else {
              obj.material.dispose();
            }
          }
        }
      });
      return [];
    };

    layersRef.current = cleanupObjects(layersRef.current);
    shapesRef.current = [];
  }, [removeVisualHelpers]);

  // Process shape intersection from model slice
  const processShapeIntersection = useCallback(
    (intersectionPoints, layerHeight) => {
      if (
        intersectionPoints.length > 3 &&
        intersectionPoints[0].hasOwnProperty("x") &&
        intersectionPoints[0].hasOwnProperty("z")
      ) {
        const points2D = intersectionPoints.map((p) => [p.x, p.z]);

        const hullPoints = hull(points2D, 30);

        if (hullPoints && hullPoints.length > 2) {
          // Create THREE.js shape
          const shape = new THREE.Shape();

          hullPoints.forEach((point, i) => {
            if (i === 0) {
              shape.moveTo(point[0], point[1]);
            } else {
              shape.lineTo(point[0], point[1]);
            }
          });
          shape.closePath();

          // Calculate average Y position for placement
          const averageY =
            intersectionPoints.reduce((sum, p) => sum + p.y, 0) /
            intersectionPoints.length;

          return {
            shape,
            position: new THREE.Vector3(0, averageY, 0),
            rotation: new THREE.Euler(Math.PI / 2, 0, 0),
          };
        }
      }
      return null;
    },
    []
  );

  // Create extruded layer from shape
  // 放在文件里原来的位置，直接整体替换这段函数
  const createLayerFromShape = useCallback(
    (shapeData, thickness) => {
      if (!shapeData) return null;

      const { shape, position, rotation } = shapeData;

      // 挤出参数
      const extrudeSettings = {
        depth: thickness,
        bevelEnabled: false,
        curveSegments: 8,
        steps: 1,
      };

      // 生成几何
      const extrudeGeometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

      // ✅ 用“蜡”的材质（深绿色半透明）
      const material = waxMaterial;

      const layer = new THREE.Mesh(extrudeGeometry, material);
      layer.position.copy(position);
      layer.rotation.copy(rotation);
      layer.position.y -= thickness / 2; // 挤出后下移一半厚度
      layer.castShadow = true;
      layer.receiveShadow = true;

      // 轮廓线
      const outlineGeometry = new THREE.EdgesGeometry(layer.geometry);
      const outline = new THREE.LineSegments(
        outlineGeometry,
        cachedMaterials.outlineMaterial
      );
      layer.add(outline);

      return layer;
    },
    // 这里就是那句：把 waxMaterial 加进依赖数组
    [cachedMaterials, waxMaterial]
  );

  // Add threaded rod to the model
  const addThreadedRod = useCallback(
    (position, height) => {
      const rodRadius = 1.4; // M3 threaded rod approx radius
      const rodGeometry = new THREE.CylinderGeometry(
        rodRadius,
        rodRadius,
        height,
        16
      );
      const rod = new THREE.Mesh(rodGeometry, cachedMaterials.rodMaterial);

      // Position the rod
      rod.position.set(position.x, height / 2, position.z);
      rod.castShadow = true;

      return rod;
    },
    [cachedMaterials]
  );

  // Add rod hole to a layer shape
  const addRodHolesToShape = useCallback((shape, rodPositions, layerY) => {
    if (!shape || rodPositions.length === 0) return shape;

    // Create a copy of the shape to modify
    const shapeCopy = shape.clone();

    // Add hole for each rod position
    rodPositions.forEach((pos) => {
      const holePath = new THREE.Path();
      const holeRadius = 1.5; // Slightly larger than rod radius for clearance

      // Create circle hole centered at rod position
      holePath.absarc(pos.x, pos.z, holeRadius, 0, Math.PI * 2, true);
      shapeCopy.holes.push(holePath);
    });

    return shapeCopy;
  }, []);

  const createHelpers = useCallback(() => {
    if (!sceneRef.current || layersRef.current.length === 0) return;

    // Remove existing helpers
    if (helpersGroupRef.current) {
      removeVisualHelpers(sceneRef.current);
      helpersGroupRef.current = null;
    }

    // Create a group to hold all layers for bounding box calculation
    const layersGroup = new THREE.Group();
    layersRef.current.forEach((layer) => {
      layersGroup.add(layer.clone());
    });

    // Calculate bounding box from the combined group
    const boundingBox = new THREE.Box3().setFromObject(layersGroup);

    // Create helpers based on the bounding box
    helpersGroupRef.current = createVisualization(
      layersGroup,
      sceneRef.current,
      boundingBox
    );

    // Clean up the temporary group
    layersGroup.children.length = 0;
  }, [createVisualization, removeVisualHelpers]);

  const generateStackedLayers = useCallback(() => {
    if (isSliderDraggingRef.current) return;
    if (!objRef.current || !sceneRef.current) return;

    clearPreviousObjects();
    initHelpers();

    const obj = objRef.current;
    const scene = sceneRef.current;
    const shapes = [];
    const layers = [];

    setProcessingStatus("Generating layers...");

    // 🔁 Get layer count and bounding box first
    const currentCount = currentLayerCountRef.current;
    const bbox = new THREE.Box3().setFromObject(obj);
    const size = bbox.getSize(new THREE.Vector3());
    const layerSpacing = size.y / currentCount;

    // ✅ Generate sliced layers
    for (let i = 0; i < currentCount; i++) {
      const layerY = bbox.min.y + i * layerSpacing;
      const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -layerY);
      const vertices = obj.geometry.attributes.position;
      const indices = obj.geometry.index;
      const intersectionPoints = [];

      processIndices(indices, vertices, obj, plane, intersectionPoints);

      const shapeData = processShapeIntersection(
        intersectionPoints,
        layerSpacing
      );
      if (!shapeData) continue;

      shapes.push(shapeData);

      let finalShape = shapeData;
      if (addThreadedRods && rodPositions.length > 0) {
        const shapeWithHoles = addRodHolesToShape(
          shapeData.shape,
          rodPositions,
          layerY
        );
        finalShape = { ...shapeData, shape: shapeWithHoles };
      }

      const layer = createLayerFromShape(finalShape, materialThickness);
      if (layer) {
        scene.add(layer);
        layers.push(layer);
      }
    }

    // ✅ Add rods
    if (addThreadedRods && rodPositions.length > 0) {
      rodPositions.forEach((pos) => {
        const rod = addThreadedRod(pos, size.y);
        scene.add(rod);
        layers.push(rod);
      });
    }

    // ...[existing code above remains unchanged]...

    // ✅ Add light blue overlay slabs using ExtrudeGeometry AFTER all layers have been generated
    const tempGroup = new THREE.Group();
    tempGroup.add(obj.clone());
    layers.forEach((l) => tempGroup.add(l.clone()));
    const overlayBoundingBox = new THREE.Box3().setFromObject(tempGroup);
    const overlaySize = overlayBoundingBox.getSize(new THREE.Vector3());
    const centerX = overlayBoundingBox.min.x + overlaySize.x / 2;
    const centerZ = overlayBoundingBox.min.z + overlaySize.z / 2;

    const overlayGroup = new THREE.Group();
    // 把原来的 overlayMaterial 替换成木头色
    const overlayMaterial = new THREE.MeshStandardMaterial({
      color: 0x8b6d3a, // 木头棕
      roughness: 0.9,
      metalness: 0.0,
      transparent: true,
      opacity: 0.35, // 半透明，能看到里面的蜡
      side: THREE.DoubleSide,
    });

    const overlayPadding = 6; // slightly more than before
    const overlayWidth = overlaySize.x + overlayPadding;
    const overlayDepth = overlaySize.z + overlayPadding;

    for (let i = 0; i < currentCount; i++) {
      const y = overlayBoundingBox.min.y + i * layerSpacing;

      const shape = new THREE.Shape();
      shape.moveTo(-overlayWidth / 2, -overlayDepth / 2);
      shape.lineTo(overlayWidth / 2, -overlayDepth / 2);
      shape.lineTo(overlayWidth / 2, overlayDepth / 2);
      shape.lineTo(-overlayWidth / 2, overlayDepth / 2);
      shape.lineTo(-overlayWidth / 2, -overlayDepth / 2);

      const extrudeSettings = {
        depth: 2.5, // slab thickness
        bevelEnabled: false,
      };

      const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
      const mesh = new THREE.Mesh(geometry, overlayMaterial);
      mesh.rotation.x = Math.PI / 2;
      mesh.position.set(centerX, y, centerZ);
      overlayGroup.add(mesh);
    }

    scene.add(overlayGroup);
    layersRef.current.push(overlayGroup);

    // 🔁 Force a render to make sure overlay appears
    rendererRef.current?.render(scene, cameraRef.current);

    // ...[rest of generateStackedLayers and file remains the same]...

    createHelpers();
    setProcessingStatus("");
    setSvgReady(true);
  }, [
    clearPreviousObjects,
    materialThickness,
    addThreadedRods,
    rodPositions,
    processShapeIntersection,
    createLayerFromShape,
    addRodHolesToShape,
    addThreadedRod,
    initHelpers,
    createHelpers,
  ]);

  // Handle slider mouse down event
  const handleSliderMouseDown = useCallback(() => {
    //console.log("Slider dragging started");
    isSliderDraggingRef.current = true;

    // Attach a global mouse up handler to detect when the user releases the slider
    const handleGlobalMouseUp = () => {
      //console.log("Slider dragging ended");
      isSliderDraggingRef.current = false;

      // Regenerate layers after slider is released
      if (regenerateLayersTimeoutRef.current) {
        clearTimeout(regenerateLayersTimeoutRef.current);
      }

      regenerateLayersTimeoutRef.current = setTimeout(() => {
        generateStackedLayers();
        regenerateLayersTimeoutRef.current = null;
      }, 50);

      // Remove global event listeners
      window.removeEventListener("mouseup", handleGlobalMouseUp);
      window.removeEventListener("touchend", handleGlobalMouseUp);
    };

    // Add global event listeners
    window.addEventListener("mouseup", handleGlobalMouseUp);
    window.addEventListener("touchend", handleGlobalMouseUp);
  }, [generateStackedLayers]);

  // Handle material thickness change
  const handleMaterialThicknessChange = useCallback(
    (e) => {
      const newValue = parseFloat(e.target.value);
      setMaterialThickness(newValue);

      // Use the same debouncing approach as the layer count slider
      if (regenerateLayersTimeoutRef.current) {
        clearTimeout(regenerateLayersTimeoutRef.current);
      }

      regenerateLayersTimeoutRef.current = setTimeout(() => {
        generateStackedLayers();
        regenerateLayersTimeoutRef.current = null;
      }, 300);
    },
    [generateStackedLayers]
  );

  // Handle model loading
  const loadModel = useCallback(() => {
    setIsLoading(true);
    setError(null);
    setSvgReady(false);

    // Cleanup any existing model
    if (objRef.current) {
      sceneRef.current?.remove(objRef.current);
      objRef.current.geometry?.dispose();
      if (Array.isArray(objRef.current.material)) {
        objRef.current.material.forEach((m) => m.dispose());
      } else {
        objRef.current.material?.dispose();
      }
      objRef.current = null;
    }

    initHelpers();

    const loader = new STLLoader();
    const modelPath = stlFile || "/models/twistT.stl";

    loader.load(
      modelPath,
      (geometry) => {
        geometry.computeBoundingBox();
        const rawSize = new THREE.Vector3();
        geometry.boundingBox.getSize(rawSize);
        const rawCenter = new THREE.Vector3();
        geometry.boundingBox.getCenter(rawCenter);

        // Translate and rotate geometry
        geometry.translate(-rawCenter.x, -rawCenter.y, -rawCenter.z);

        const material = cachedMaterials.originalModelMaterial;
        const mesh = new THREE.Mesh(geometry, material);
        normalizeModel(mesh);

        // Compute final bounding box after transforms
        const finalBox = new THREE.Box3().setFromObject(mesh);
        const finalSize = new THREE.Vector3();
        finalBox.getSize(finalSize);

        // Shift mesh to sit flat on grid
        mesh.position.y -= finalBox.min.y;
        mesh.updateMatrixWorld(true);

        // Store the model and size
        objRef.current = mesh;
        // sceneRef.current?.add(mesh);

        setModelDimensions({
          width: finalSize.x,
          height: finalSize.y,
          depth: finalSize.z,
        });

        // Set layers based on transformed height
        const calculatedMaxLayers = calculateMaxLayerCount(
          finalSize.y,
          materialThickness
        );
        setMaxLayerCount(calculatedMaxLayers);
        setLayerCount(calculatedMaxLayers);
        currentLayerCountRef.current = calculatedMaxLayers;

        setIsLoading(false);
        setTimeout(() => {
          generateStackedLayers();
        }, 100);
      },
      (xhr) => {
        const percentComplete = (xhr.loaded / xhr.total) * 100;
        setProcessingStatus(`Loading model: ${Math.round(percentComplete)}%`);
      },
      (error) => {
        //console.error(`Error loading STL from ${modelPath}:`, error);

        // Create a default cube for testing if model fails to load
        const geometry = new THREE.BoxGeometry(30, 30, 30);
        const material = cachedMaterials.originalModelMaterial;

        const mesh = new THREE.Mesh(geometry, material);
        mesh.castShadow = true;
        mesh.receiveShadow = true;

        // Save dimensions for SVG generation
        setModelDimensions({
          width: 30,
          height: 30,
          depth: 30,
        });

        // Calculate max layer count and set initial layer count to that value
        const calculatedMaxLayers = calculateMaxLayerCount(
          30,
          materialThickness
        );
        setMaxLayerCount(calculatedMaxLayers);
        setLayerCount(calculatedMaxLayers); // Set the layer count to the maximum
        currentLayerCountRef.current = calculatedMaxLayers; // Also update the ref

        objRef.current = mesh;
        mesh.visible = false; // Hide the original model
        //sceneRef.current?.add(mesh);

        setIsLoading(false);

        // Generate layers after a short delay
        setTimeout(() => {
          generateStackedLayers();
        }, 100);
      }
    );
  }, [
    stlFile,
    generateStackedLayers,
    cachedMaterials,
    initHelpers,
    calculateMaxLayerCount,
    materialThickness,
  ]);

  // Handle SVG download
  const handleDownloadSVG = useCallback(() => {
    if (!shapesRef.current.length) return;

    const svgData = generateSVG(
      shapesRef.current,
      rodPositions,
      modelDimensions,
      materialThickness
    );

    downloadSVG(svgData, "stacked-layers");

    // Show tutorial after download
    setTutorialVisible(true);
  }, [modelDimensions, materialThickness, rodPositions]);

  // Handle mouse click for rod placement
  const handleCanvasClick = useCallback(
    (event) => {
      if (!addThreadedRods || !objRef.current || !sceneRef.current) return;

      // Calculate mouse position in normalized device coordinates
      const rect = containerRef.current.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      // Create raycaster
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(new THREE.Vector2(x, y), cameraRef.current);

      // Check for intersection with the model
      const intersects = raycaster.intersectObject(objRef.current);

      if (intersects.length > 0) {
        const point = intersects[0].point;

        // Check if we're removing an existing rod
        const existingRodIndex = rodPositions.findIndex(
          (pos) => pos.distanceTo(point) < 5 // 5 units threshold for removal
        );

        if (existingRodIndex >= 0) {
          // Remove the rod
          const newPositions = [...rodPositions];
          newPositions.splice(existingRodIndex, 1);
          setRodPositions(newPositions);
        } else {
          // Add a new rod
          setRodPositions((prev) => [...prev, point.clone()]);
        }

        // Regenerate the layers
        setTimeout(() => {
          generateStackedLayers();
        }, 100);
      }
    },
    [addThreadedRods, rodPositions, generateStackedLayers]
  );

  // Initialize Three.js scene
  useEffect(() => {
    const cleanup = initializeThreeScene(
      containerRef,
      sceneRef,
      rendererRef,
      cameraRef,
      controlsRef
    );

    // Load model after scene is initialized
    setTimeout(() => {
      loadModel();
    }, 0);

    return () => {
      // Clean up visual helpers before unmounting
      if (sceneRef.current) {
        removeVisualHelpers(sceneRef.current);
      }

      // Clear any pending timeouts
      if (regenerateLayersTimeoutRef.current) {
        clearTimeout(regenerateLayersTimeoutRef.current);
      }

      cleanup();
    };
  }, [loadModel, removeVisualHelpers]);

  // Re-load model when stlFile changes
  useEffect(() => {
    if (stlFile && sceneRef.current) {
      loadModel();
    }
  }, [stlFile, loadModel]);

  // Modified effect for material thickness changes
  useEffect(() => {
    // This will update dependent values like maxLayerCount but not trigger regeneration
    if (modelDimensions) {
      const newMaxLayerCount = calculateMaxLayerCount(
        modelDimensions.height,
        materialThickness
      );
      setMaxLayerCount(newMaxLayerCount);

      // Adjust current layer count if it exceeds the new maximum
      if (layerCount > newMaxLayerCount) {
        setLayerCount(newMaxLayerCount);
        currentLayerCountRef.current = newMaxLayerCount;
      }
    }
  }, [materialThickness, modelDimensions, calculateMaxLayerCount, layerCount]);

  // Add/remove click handler for threaded rods
  useEffect(() => {
    const container = containerRef.current;

    if (addThreadedRods && container) {
      container.addEventListener("click", handleCanvasClick);
    }

    return () => {
      if (container) {
        container.removeEventListener("click", handleCanvasClick);
      }
    };
  }, [addThreadedRods, handleCanvasClick]);

  // Effect for handling rod position changes
  useEffect(() => {
    if (addThreadedRods && objRef.current) {
      // Only regenerate when rod positions change
      generateStackedLayers();
    }
  }, [rodPositions, addThreadedRods, generateStackedLayers]);

  return (
    <div className="container">
      <div ref={containerRef} className="canvas-container"></div>
      <button
        className="panel-toggle-button"
        onClick={() => setExpanded((prev) => !prev)}
      >
        {expanded ? "⟨ Hide Panel" : "⟩ Show Panel"}
      </button>

      {/* Floating Control Panel */}
      {!isLoading && expanded && (
        <div className="controls-panel">
          <div className="panel-title-row">
  <div className="panel-title">Stack Mold</div>
  {fastIterNotRecommended && (
    <span
      className="iteration-badge badge--warn"
      title="Stacking + gluing/clamping + finishing = lots of manual time"
    >
      Not ideal for quick iteration
    </span>
  )}
</div>


          {/* Information section */}
          <div className="panel-section">
            <div className="section-header">
              <h4>Workflow Description</h4>
            </div>
            <div className="section-content">
 <p>
      Based on the workflow from <em>StackMold</em>, this method slices your 3D
      model into horizontal layers to create a stacked, laser-cut mold. This
      layered mold can be used for step-by-step casting, enabling complex
      geometries to be formed in multiple pours.
    </p>
    <p style={{ fontStyle: "italic" }}>
      Ideal for wood, MDF, or acrylic sheets. Threaded rods can be added for
      precise alignment and structural support during casting.
    </p>
            </div>
          </div>

          {/* Time & Effort (Manual Labor Time) */}
<div className="panel-section">
  <div className="section-header"><h4>Time & Effort</h4></div>
  <div className="section-content">
    <ul style={{ marginLeft: 16, lineHeight: 1.5, fontSize: 13 }}>
      <li><strong>Laser cutting</strong>: Cutting and numbering multiple material sheets.</li>
      <li><strong>Stacking & alignment</strong>: Layer-by-layer positioning, gluing/fixture securing.</li>
      <li><strong>Finishing</strong>: Sanding, leveling, edge trimming, possible multiple reworks.</li>
      <li><strong>Optional casting</strong>: Applying release agent, pouring, curing and demolding.</li>
    </ul>
    <p style={{ fontSize: 12, opacity: 0.85, marginTop: 6 }}>
      Recommend first using fewer layers/small-scale models to verify positioning and form, then proceed to full size.
    </p>
  </div>
</div>

          {/* Sliders and checkbox settings remain below */}

          {/* Collapsible Section Toggle */}
          <div
            className="section-toggle"
            onClick={() => setSettingsOpen((v) => !v)}   // ✅ 不再动 expanded
>
            stacked_layers_settings
          </div>

          {/* Panel content - conditionally rendered based on expanded state */}
{settingsOpen && (
            <div className="panel-content">
              {/*               <div className="input-group">
                <label>Number of Layers</label>
                <div className="value-display">
                  <input
                    type="range"
                    className="slider"
                    min="3"
                    max={maxLayerCount}
                    value={layerCount}
                    onChange={handleLayerCountChange}
                    onMouseDown={handleSliderMouseDown}
                    onTouchStart={handleSliderMouseDown}
                    disabled={isLoading}
                  />
                  <div className="value">
                    {layerCount}
                  </div>
                </div>
              </div> */}

              <div className="input-group">
                <label>Material Thickness (mm)</label>
                <div className="value-display">
                  <input
                    type="range"
                    className="slider"
                    min="1"
                    max="5"
                    step="0.5"
                    value={materialThickness}
                    onChange={handleMaterialThicknessChange}
                    disabled={isLoading}
                  />
                  <div className="value">{materialThickness}</div>
                </div>
              </div>

              {/*   <div className="checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={addThreadedRods}
                    onChange={(e) => setAddThreadedRods(e.target.checked)}
                    disabled={isLoading}
                  />
                  Add Threaded Rods
                </label>
                {addThreadedRods && (
                  <div className="help-text">
                    Click on the model to add/remove rod positions
                  </div>
                )}
              </div> */}
            </div>
          )}

          {/* Buttons outside the collapsible section */}
          <div className="buttons-container">
           {/*  <button
              onClick={handleDownloadSVG}
              className="action-button"
              disabled={!svgReady || isLoading}
            >
              DOWNLOAD SVG FILES
            </button> */}
            <button onClick={toggleTutorial} className="action-button">
              {tutorialVisible
                ? "HIDE INTERACTIVE GUIDANCE"
                : "SHOW INTERACTIVE GUIDANCE"}
            </button>
          </div>
        </div>
      )}

      {/* Loading Message */}
      {isLoading && <div className="loading-box">Loading model...</div>}

      {/* Processing Status */}
      {processingStatus && (
        <div className="loading-box">{processingStatus}</div>
      )}

      {/* Error Message */}
      {error && <div className="error-box">Error: {error}</div>}

      {/* Rod Placement Instructions */}
      {addThreadedRods && (
        <div className="instructions-box">
          <div className="title">Rod Placement</div>
          <div className="instruction">
            Click on the model to add rod positions
          </div>
          <div className="instruction">
            Click on an existing rod position to remove it
          </div>
          <div className="rod-count">Rods: {rodPositions.length}</div>
        </div>
      )}

      <div className="legend">
        <div className="legend-row">
          <span
            className="swatch"
            style={{ background: "#8B6D3A", opacity: 0.8 }}
          />
          Exterior: Wooden mold (translucent)
        </div>
        <div className="legend-row">
          <span
            className="swatch"
            style={{ background: "#0A7A32", opacity: 0.8 }}
          />
          Interior: Wax (casting)
        </div>
      </div>

      <TutorialWindow
        visible={tutorialVisible}
        onToggleVisibility={toggleTutorial}
        tutorialContent={stackMoldTutorial}
      />
    </div>
  );
};

export default StackMold;
